import React, { useState, useEffect, useMemo } from "react";
import {
  Opportunity,
  Requirement,
  DraftSection,
  FormatRule,
  RequiredForm,
  UserProfile,
  CoachReviewRecord,
  CoachFinding,
} from "../../types";
import { subscribeToRequirements } from "../../services/requirementsService";
import {
  subscribeToRequiredForms,
  subscribeToFormatRules,
} from "../../services/requirementsService";
import { subscribeToDraftSections } from "../../services/draftsService";
import { subscribeToLatestCoachReview, runCoachReview } from "../../services/coachService";
import {
  copyProposalForGoogleDocs,
  downloadProposalMarkdown,
  generateMarkdownProposal,
  CompiledProposal,
} from "../../utils/exportUtils";
import { ExpandableCard } from "../common/ExpandableCard";
import {
  Sparkles,
  Copy,
  Download,
  Eye,
  CheckCircle2,
  AlertTriangle,
  FileText,
  Loader2,
  Check,
  ArrowRight,
  ShieldCheck,
  X,
} from "lucide-react";

interface CoachTabProps {
  opportunity: Opportunity;
  user: UserProfile | null;
  onApplySuggestion?: (requirementId: string, text: string) => void;
  onSwitchTab?: (tab: string) => void;
}

export const CoachTab: React.FC<CoachTabProps> = ({
  opportunity,
  user: _user,
  onApplySuggestion,
  onSwitchTab,
}) => {
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [draftSections, setDraftSections] = useState<DraftSection[]>([]);
  const [formatRules, setFormatRules] = useState<FormatRule[]>([]);
  const [requiredForms, setRequiredForms] = useState<RequiredForm[]>([]);
  const [latestReview, setLatestReview] = useState<CoachReviewRecord | null>(null);

  const [isAuditing, setIsAuditing] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [copiedGoogleDocs, setCopiedGoogleDocs] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Filters
  const [severityFilter, setSeverityFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");

  // 1. Live Subscriptions
  useEffect(() => {
    const unsub = subscribeToRequirements(opportunity.id, setRequirements);
    return () => unsub();
  }, [opportunity.id]);

  useEffect(() => {
    const unsub = subscribeToDraftSections(opportunity.id, setDraftSections);
    return () => unsub();
  }, [opportunity.id]);

  useEffect(() => {
    const unsub = subscribeToFormatRules(opportunity.id, setFormatRules);
    return () => unsub();
  }, [opportunity.id]);

  useEffect(() => {
    const unsub = subscribeToRequiredForms(opportunity.id, setRequiredForms);
    return () => unsub();
  }, [opportunity.id]);

  useEffect(() => {
    const unsub = subscribeToLatestCoachReview(opportunity.id, setLatestReview);
    return () => unsub();
  }, [opportunity.id]);

  // Compiled proposal for export / preview
  const compiledProposal: CompiledProposal = useMemo(
    () => ({
      opportunity,
      requirements,
      draftSections,
      formatRules,
      requiredForms,
    }),
    [opportunity, requirements, draftSections, formatRules, requiredForms],
  );

  // Pre-submission metrics
  const unapprovedSections = useMemo(
    () => draftSections.filter((s) => s.status !== "approved"),
    [draftSections],
  );

  const actionNeededItems = useMemo(() => {
    const list: Array<{ sectionTitle: string; item: string }> = [];
    draftSections.forEach((sec) => {
      const matches = sec.content.match(/\[ACTION NEEDED:[^\]]+\]/gi);
      if (matches) {
        matches.forEach((m) => list.push({ sectionTitle: sec.title, item: m }));
      }
    });
    return list;
  }, [draftSections]);

  const unansweredReqCount = requirements.filter(
    (r) => !draftSections.some((s) => s.requirementId === r.id && s.content.trim().length > 0),
  ).length;

  const incompleteFormsCount = requiredForms.filter(
    (f) => f.mandatory && (f.status === "not_started" || f.status === "in_progress"),
  ).length;

  // Filtered findings
  const filteredFindings = useMemo(() => {
    if (!latestReview?.findings) return [];
    return latestReview.findings.filter((f) => {
      if (severityFilter !== "all" && f.severity !== severityFilter) {
        return false;
      }
      if (categoryFilter !== "all" && f.category !== categoryFilter) {
        return false;
      }
      return true;
    });
  }, [latestReview, severityFilter, categoryFilter]);

  // Run Coach Review
  const handleRunAudit = async () => {
    setIsAuditing(true);
    setAuditError(null);
    try {
      await runCoachReview({
        opportunityId: opportunity.id,
        requirements,
        draftSections,
        formatRules,
        requiredForms,
      });
    } catch (err) {
      console.error("Coach review audit error:", err);
      setAuditError(err instanceof Error ? err.message : "Failed to run Proposal Coach audit.");
    } finally {
      setIsAuditing(false);
    }
  };

  // Google Docs HTML Clipboard Copy
  const handleCopyGoogleDocs = async () => {
    try {
      await copyProposalForGoogleDocs(compiledProposal);
      setCopiedGoogleDocs(true);
      setTimeout(() => setCopiedGoogleDocs(false), 3000);
    } catch (err) {
      console.error("Failed to copy proposal:", err);
      setAuditError(err instanceof Error ? err.message : "Failed to copy proposal to clipboard.");
    }
  };

  // Download Markdown
  const handleDownloadMarkdown = () => {
    downloadProposalMarkdown(compiledProposal);
  };

  // Apply suggestion
  const handleApply = (finding: CoachFinding) => {
    if (finding.ruleOrReqId && finding.suggestedFix && onApplySuggestion) {
      onApplySuggestion(finding.ruleOrReqId, finding.suggestedFix);
    }
    if (onSwitchTab) {
      onSwitchTab("drafting");
    }
  };

  // Score styling
  const score = latestReview ? latestReview.overallScore : 0;
  let scoreBadgeColor =
    "text-rose-600 bg-rose-50 border-rose-200 dark:bg-rose-950/40 dark:border-rose-900 dark:text-rose-400";
  if (score >= 80) {
    scoreBadgeColor =
      "text-emerald-600 bg-emerald-50 border-emerald-200 dark:bg-emerald-950/40 dark:border-emerald-900 dark:text-emerald-400";
  } else if (score >= 50) {
    scoreBadgeColor =
      "text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-950/40 dark:border-amber-900 dark:text-amber-400";
  }

  return (
    <div className="space-y-6">
      {/* Top Header & Export Toolbar */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-slate-200 pb-4 dark:border-slate-800">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#2ba5b5]" />
            <span>Proposal Writing Coach & Export</span>
          </h2>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Automated compliance, format, tone, and gap review with clean Google Docs export.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Run Audit Button */}
          <button
            type="button"
            onClick={handleRunAudit}
            disabled={isAuditing}
            className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-3.5 py-2 text-xs font-semibold text-white hover:bg-[#238997] disabled:opacity-50 shadow-xs"
          >
            {isAuditing ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span>Auditing Proposal...</span>
              </>
            ) : (
              <>
                <Sparkles className="h-3.5 w-3.5" />
                <span>Run Proposal Coach Audit</span>
              </>
            )}
          </button>

          {/* Copy for Google Docs Button */}
          <button
            type="button"
            onClick={handleCopyGoogleDocs}
            className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 shadow-2xs"
          >
            {copiedGoogleDocs ? (
              <>
                <Check className="h-3.5 w-3.5 text-emerald-600" />
                <span className="text-emerald-600 font-bold">Copied for Google Docs!</span>
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5 text-slate-400" />
                <span>Copy for Google Docs</span>
              </>
            )}
          </button>

          {/* Download Markdown */}
          <button
            type="button"
            onClick={handleDownloadMarkdown}
            className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            title="Download standard Markdown file"
          >
            <Download className="h-3.5 w-3.5 text-slate-400" />
            <span className="hidden sm:inline">Markdown</span>
          </button>

          {/* Preview Full Proposal */}
          <button
            type="button"
            onClick={() => setShowPreviewModal(true)}
            className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            title="Preview full proposal draft"
          >
            <Eye className="h-3.5 w-3.5 text-slate-400" />
            <span className="hidden sm:inline">Preview</span>
          </button>
        </div>
      </div>

      {auditError && (
        <div className="flex items-center justify-between rounded-lg bg-rose-50 px-4 py-2.5 text-xs font-medium text-rose-800 border border-rose-200 dark:bg-rose-950/50 dark:border-rose-900 dark:text-rose-200">
          <span>{auditError}</span>
          <button
            type="button"
            onClick={() => setAuditError(null)}
            className="text-rose-600 hover:text-rose-800"
          >
            ×
          </button>
        </div>
      )}

      {/* Audit Readiness & Score Strip */}
      <div className="grid gap-4 md:grid-cols-12">
        {/* Score & Passed Checks Card (4 cols) */}
        <div className="glass rounded-xl border border-slate-200/80 p-5 dark:border-slate-800/80 md:col-span-4 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Compliance Score
              </span>
              {latestReview && (
                <span className="text-[11px] text-slate-400">
                  Model: {latestReview.modelUsed || "Gemini"}
                </span>
              )}
            </div>

            <div className="mt-3 flex items-baseline gap-3">
              <div
                className={`flex h-16 w-16 items-center justify-center rounded-2xl border text-2xl font-black ${scoreBadgeColor}`}
              >
                {latestReview ? `${score}` : "--"}
              </div>
              <div>
                <p className="text-sm font-bold text-slate-800 dark:text-slate-100">
                  {latestReview
                    ? score >= 80
                      ? "High Readiness"
                      : score >= 50
                        ? "Moderate Quality"
                        : "Substantial Gaps"
                    : "No Audit Performed"}
                </p>
                <p className="text-xs text-slate-500">
                  {latestReview
                    ? `${latestReview.passedChecksCount} of ${latestReview.totalChecksCount} checks passed`
                    : "Click 'Run Proposal Coach Audit' to evaluate."}
                </p>
              </div>
            </div>
          </div>

          {latestReview && (
            <div className="mt-4 pt-3 border-t border-slate-100 text-[11px] text-slate-400 dark:border-slate-800">
              Last audited: {new Date(latestReview.generatedAt).toLocaleString()}
            </div>
          )}
        </div>

        {/* Coach Executive Summary Card (8 cols) */}
        <div className="glass rounded-xl border border-slate-200/80 p-5 dark:border-slate-800/80 md:col-span-8 flex flex-col justify-between">
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Writing Coach Executive Summary
            </span>
            <p className="mt-2 text-xs leading-relaxed text-slate-700 dark:text-slate-300">
              {latestReview?.summary ||
                "Run the proposal coach to automatically review all draft sections against RFP requirements, client specifications, formatting restrictions, and tone standards."}
            </p>
          </div>

          {/* Quick Metrics Bar */}
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4 pt-3 border-t border-slate-100 dark:border-slate-800 text-xs">
            <div className="rounded-lg bg-slate-50 p-2 text-center dark:bg-slate-900/60">
              <span className="text-[10px] text-slate-400 block uppercase">Unanswered</span>
              <span
                className={`font-bold ${unansweredReqCount > 0 ? "text-amber-600" : "text-emerald-600"}`}
              >
                {unansweredReqCount}
              </span>
            </div>
            <div className="rounded-lg bg-slate-50 p-2 text-center dark:bg-slate-900/60">
              <span className="text-[10px] text-slate-400 block uppercase">Unapproved</span>
              <span
                className={`font-bold ${unapprovedSections.length > 0 ? "text-amber-600" : "text-emerald-600"}`}
              >
                {unapprovedSections.length}
              </span>
            </div>
            <div className="rounded-lg bg-slate-50 p-2 text-center dark:bg-slate-900/60">
              <span className="text-[10px] text-slate-400 block uppercase">Action Needed</span>
              <span
                className={`font-bold ${actionNeededItems.length > 0 ? "text-rose-600" : "text-emerald-600"}`}
              >
                {actionNeededItems.length}
              </span>
            </div>
            <div className="rounded-lg bg-slate-50 p-2 text-center dark:bg-slate-900/60">
              <span className="text-[10px] text-slate-400 block uppercase">Missing Forms</span>
              <span
                className={`font-bold ${incompleteFormsCount > 0 ? "text-rose-600" : "text-emerald-600"}`}
              >
                {incompleteFormsCount}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Pre-Submission Readiness Alerts */}
      {(actionNeededItems.length > 0 ||
        unapprovedSections.length > 0 ||
        incompleteFormsCount > 0) && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200 space-y-2">
          <div className="flex items-center gap-2 font-bold text-amber-800 dark:text-amber-300">
            <AlertTriangle className="h-4 w-4" />
            <span>Pre-Submission Warning Callouts</span>
          </div>
          <div className="grid gap-2 sm:grid-cols-3 pt-1">
            {unapprovedSections.length > 0 && (
              <div className="rounded-lg bg-white/70 p-2.5 dark:bg-slate-900/60 border border-amber-200/50">
                <span className="font-semibold block">
                  {unapprovedSections.length} Unapproved Sections
                </span>
                <span className="text-[11px] text-amber-700 dark:text-amber-400">
                  Sections must be marked 'Approved' prior to submission.
                </span>
              </div>
            )}
            {actionNeededItems.length > 0 && (
              <div className="rounded-lg bg-white/70 p-2.5 dark:bg-slate-900/60 border border-rose-200/50 text-rose-800 dark:text-rose-300">
                <span className="font-semibold block">
                  {actionNeededItems.length} Unresolved Action Items
                </span>
                <span className="text-[11px]">
                  Drafts contain bracketed placeholders [ACTION NEEDED: ...]
                </span>
              </div>
            )}
            {incompleteFormsCount > 0 && (
              <div className="rounded-lg bg-white/70 p-2.5 dark:bg-slate-900/60 border border-amber-200/50">
                <span className="font-semibold block">
                  {incompleteFormsCount} Mandatory Forms Incomplete
                </span>
                <span className="text-[11px] text-amber-700 dark:text-amber-400">
                  Required forms in intake must be completed.
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filter Bar for Findings */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-semibold text-slate-500 mr-1">Severity:</span>
          {[
            { id: "all", label: "All Severities" },
            { id: "high", label: "High" },
            { id: "medium", label: "Medium" },
            { id: "low", label: "Low" },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setSeverityFilter(tab.id)}
              className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-all ${
                severityFilter === tab.id
                  ? "bg-[#2ba5b5] text-white shadow-2xs"
                  : "bg-slate-100 text-slate-600 hover:text-slate-900 dark:bg-slate-800 dark:text-slate-300"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-semibold text-slate-500 mr-1">Category:</span>
          {[
            { id: "all", label: "All" },
            { id: "compliance", label: "Compliance" },
            { id: "format", label: "Format" },
            { id: "tone_clarity", label: "Tone & Clarity" },
            { id: "gap", label: "Gaps" },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setCategoryFilter(tab.id)}
              className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-all ${
                categoryFilter === tab.id
                  ? "bg-slate-800 text-white dark:bg-slate-700"
                  : "bg-slate-100 text-slate-600 hover:text-slate-900 dark:bg-slate-800 dark:text-slate-300"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Findings List (Expandable Cards) */}
      {!latestReview ? (
        <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center dark:border-slate-800">
          <ShieldCheck className="mx-auto h-8 w-8 text-slate-400" />
          <p className="mt-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
            No proposal audit has been generated yet.
          </p>
          <p className="mt-1 text-xs text-slate-400 max-w-md mx-auto">
            Click "Run Proposal Coach Audit" above to analyze all drafts against client requirements
            and formatting guidelines.
          </p>
        </div>
      ) : filteredFindings.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-emerald-200 bg-emerald-50/30 p-8 text-center dark:border-emerald-900 dark:bg-emerald-950/20">
          <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-600 dark:text-emerald-400" />
          <p className="mt-2 text-sm font-semibold text-emerald-800 dark:text-emerald-300">
            Zero findings match your current filter.
          </p>
          <p className="mt-1 text-xs text-emerald-600/80">
            All checks passed for the selected severity and category!
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredFindings.map((finding, idx) => {
            let severityBadgeColor =
              "bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300";
            if (finding.severity === "medium") {
              severityBadgeColor =
                "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300";
            } else if (finding.severity === "low") {
              severityBadgeColor =
                "bg-teal-100 text-teal-800 dark:bg-teal-950/60 dark:text-teal-300";
            }

            const categoryLabel = finding.category.replace(/_/g, " ").toUpperCase();
            const targetReq = requirements.find((r) => r.id === finding.ruleOrReqId);
            const targetTitle = targetReq
              ? `${targetReq.sectionRef ? targetReq.sectionRef + " " : ""}${targetReq.title}`
              : "Opportunity Level";

            return (
              <ExpandableCard
                key={`${finding.category}-${finding.ruleOrReqId || idx}-${idx}`}
                title={targetTitle}
                badge={`${finding.severity.toUpperCase()} PRIORITY`}
                badgeColor={severityBadgeColor}
                summary={
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="font-semibold text-slate-600 dark:text-slate-300">
                      {categoryLabel}
                    </span>
                    <span>•</span>
                    <span className="text-slate-500 line-clamp-1">{finding.message}</span>
                  </div>
                }
                defaultExpanded={false}
                headerActions={
                  finding.suggestedFix ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleApply(finding);
                      }}
                      className="btn-animate inline-flex items-center gap-1 rounded-lg border border-teal-200 bg-teal-50 px-2.5 py-1 text-xs font-semibold text-[#2ba5b5] hover:bg-teal-100 dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-300"
                    >
                      <span>Apply Suggestion</span>
                      <ArrowRight className="h-3 w-3" />
                    </button>
                  ) : undefined
                }
                details={
                  <div className="space-y-3 pt-2 text-xs">
                    <div>
                      <span className="font-bold text-slate-700 dark:text-slate-300">
                        Auditor Finding:
                      </span>
                      <p className="mt-1 leading-relaxed text-slate-600 dark:text-slate-400">
                        {finding.message}
                      </p>
                    </div>

                    {finding.suggestedFix && (
                      <div className="rounded-xl border border-teal-200 bg-teal-50/50 p-3.5 dark:border-teal-900 dark:bg-teal-950/30">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="font-bold text-[#2ba5b5]">
                            Suggested Fix (Staged for Review):
                          </span>
                          <button
                            type="button"
                            onClick={() => handleApply(finding)}
                            className="btn-animate inline-flex items-center gap-1 rounded-md bg-[#2ba5b5] px-3 py-1 font-semibold text-white hover:bg-[#238997]"
                          >
                            <span>Apply to Draft</span>
                            <ArrowRight className="h-3 w-3" />
                          </button>
                        </div>
                        <p className="font-mono text-[11px] leading-relaxed text-slate-800 dark:text-slate-200 whitespace-pre-wrap">
                          {finding.suggestedFix}
                        </p>
                      </div>
                    )}
                  </div>
                }
              />
            );
          })}
        </div>
      )}

      {/* Full Proposal Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-xs">
          <div className="relative flex max-h-[90vh] w-full max-w-4xl flex-col rounded-2xl bg-white shadow-2xl dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-[#2ba5b5]" />
                <h3 className="font-bold text-slate-900 dark:text-white">
                  Proposal Document Preview ({opportunity.title})
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowPreviewModal(false)}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body: Markdown rendered representation */}
            <div className="flex-1 overflow-y-auto p-6 font-mono text-xs leading-relaxed text-slate-800 dark:text-slate-200 space-y-4">
              <pre className="whitespace-pre-wrap font-sans text-xs bg-slate-50 p-4 rounded-xl border border-slate-100 dark:bg-slate-950 dark:border-slate-800">
                {generateMarkdownProposal(compiledProposal)}
              </pre>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between border-t border-slate-200 px-6 py-3 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/60 rounded-b-2xl">
              <span className="text-xs text-slate-500">
                {draftSections.length} section(s) •{" "}
                {draftSections.reduce((sum, s) => sum + s.wordCount, 0)} total words
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCopyGoogleDocs}
                  className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                >
                  {copiedGoogleDocs ? (
                    <Check className="h-3.5 w-3.5 text-emerald-600" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                  <span>{copiedGoogleDocs ? "Copied!" : "Copy for Google Docs"}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setShowPreviewModal(false)}
                  className="btn-animate rounded-lg bg-[#2ba5b5] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#238997]"
                >
                  Close Preview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
